/**
 * Application configuration
 *
 * This is the single source of truth for environment-specific settings.
 * Backend API URL is configured via VITE_BACKEND_URL environment variable.
 */

/**
 * Storage Configuration
 *
 * Controls sessionStorage LRU cache behavior to prevent QuotaExceededError
 * on iOS Safari and other browsers with strict storage limits (~5-10MB).
 */
export const STORAGE_CONFIG = {
  /**
   * Maximum number of thread message histories to keep in sessionStorage.
   * Older threads are automatically evicted when this limit is reached.
   * Default: 5 threads (allows ~1-2MB per thread on average)
   */
  MAX_CACHED_THREADS: 5,

  /**
   * Threads older than this many days are automatically removed on app startup.
   * Default: 7 days
   */
  STALE_THRESHOLD_DAYS: 7,

  /**
   * Enable console warnings for storage operations (cleanup, eviction, errors).
   * Useful for debugging storage issues.
   * Default: true in development, false in production
   */
  ENABLE_STORAGE_WARNINGS: import.meta.env.DEV,
} as const;

/**
 * Feature Flags
 *
 * Controls which features are enabled/disabled in the application.
 * IT admins can control these via environment variables.
 */
export const FEATURES = {
  /**
   * Enable file upload functionality in chat input.
   * Allows users to upload images (JPEG, PNG, GIF, WebP) and PDFs.
   * Set VITE_FEATURE_FILE_UPLOAD_ENABLED=true in .env to enable.
   */
  fileUploadEnabled: import.meta.env.VITE_FEATURE_FILE_UPLOAD_ENABLED === 'true',

  /**
   * Enable thumbs up/down feedback buttons below assistant messages.
   * Set VITE_FEATURE_FEEDBACK_ENABLED=true in .env to enable.
   */
  feedbackEnabled: import.meta.env.VITE_FEATURE_FEEDBACK_ENABLED === 'true',

  /**
   * Enable language switcher in InfoModal.
   * Shows language toggle for switching between languages.
   * Set VITE_FEATURE_LANGUAGE_SWITCHER_ENABLED=true in .env to enable.
   */
  languageSwitcherEnabled: import.meta.env.VITE_FEATURE_LANGUAGE_SWITCHER_ENABLED === 'true',

  /**
   * Enable Firebase Authentication for email/password login.
   * When enabled, users must authenticate before using the app.
   * Set VITE_FEATURE_AUTH_ENABLED=true in .env to enable.
   */
  authEnabled: import.meta.env.VITE_FEATURE_AUTH_ENABLED === 'true',

  /**
   * Enable theme switcher in InfoModal.
   * When disabled, users cannot change between light/dark theme.
   * Set VITE_FEATURE_THEME_SWITCH_ENABLED=true in .env to enable.
   */
  themeSwitchEnabled: import.meta.env.VITE_FEATURE_THEME_SWITCH_ENABLED === 'true',

  /**
   * Enable audio recording functionality in chat input.
   * Allows users to record voice messages and upload them to Firebase Storage.
   * Set VITE_FEATURE_AUDIO_RECORDING_ENABLED=true in .env to enable.
   */
  audioRecordingEnabled: import.meta.env.VITE_FEATURE_AUDIO_RECORDING_ENABLED === 'true',

  /**
   * Enable Gebreken (building code articles) search page.
   * Shows Bouwbesluit article search in the Gebreken tab.
   * Set VITE_FEATURE_GEBREKEN_PAGE_ENABLED=true in .env to enable.
   */
  gebrekenPageEnabled: import.meta.env.VITE_FEATURE_GEBREKEN_PAGE_ENABLED === 'true',
} as const;

/**
 * UI Layout Configuration
 *
 * Controls the layout and sizing of UI panels.
 * IT admins can control these via environment variables.
 */
export const UI_LAYOUT = {
  /**
   * Artifact panel width on desktop as a percentage (0-100).
   * The chat panel will take up the remaining space.
   * Default: 60 (artifact takes 60%, chat takes 40%)
   * Set VITE_ARTIFACT_PANEL_WIDTH_DESKTOP in .env to customize.
   */
  artifactPanelWidthDesktop: parseInt(
    import.meta.env.VITE_ARTIFACT_PANEL_WIDTH_DESKTOP || '60',
    10
  ),

  /**
   * Artifact panel width on tablet as a percentage (0-100).
   * Used for tablet landscape split-view.
   * Default: 50 (50/50 split)
   * Set VITE_ARTIFACT_PANEL_WIDTH_TABLET in .env to customize.
   */
  artifactPanelWidthTablet: parseInt(
    import.meta.env.VITE_ARTIFACT_PANEL_WIDTH_TABLET || '50',
    10
  ),

  /**
   * Artifact panel width on mobile in viewport width units.
   * Default: 95 (95vw)
   * Set VITE_ARTIFACT_PANEL_WIDTH_MOBILE in .env to customize.
   */
  artifactPanelWidthMobile: parseInt(
    import.meta.env.VITE_ARTIFACT_PANEL_WIDTH_MOBILE || '95',
    10
  ),

  /**
   * Header height on mobile in pixels.
   * Default: 48 (h-12)
   */
  headerHeightMobile: 48,

  /**
   * Header height on tablet in pixels.
   * Default: 56 (h-14)
   */
  headerHeightTablet: 56,

  /**
   * Sidebar width on tablet for inline sidebars.
   * Default: 280
   */
  sidebarWidthTablet: 280,
} as const;

/**
 * Pagination Configuration
 *
 * Controls how many items are displayed per page across different features.
 */
export const PAGINATION_CONFIG = {
  /**
   * Number of messages to display per page in chat thread.
   * Default: 50 messages
   */
  MESSAGES_PER_PAGE: 50,
} as const;

/**
 * Timing Configuration
 *
 * Controls various timing-related behaviors in the application.
 */
export const TIMING_CONFIG = {
  /**
   * Minimum duration to display initial chat loader (prevents flash).
   * Default: 800ms
   */
  MINIMUM_LOADER_DURATION_MS: 800,

  /**
   * Thread list sync polling configuration.
   * Used when syncing thread list after creating a new thread.
   */
  THREAD_SYNC: {
    /**
     * Initial delay before first poll attempt.
     * Default: 500ms
     */
    INITIAL_DELAY_MS: 500,

    /**
     * Maximum delay between poll attempts (exponential backoff cap).
     * Default: 8000ms (8 seconds)
     */
    MAX_DELAY_MS: 8000,

    /**
     * Maximum number of poll attempts before giving up.
     * Default: 5 attempts
     */
    MAX_ATTEMPTS: 5,

    /**
     * Multiplier for exponential backoff between attempts.
     * Delay doubles each attempt: 500ms → 1s → 2s → 4s → 8s
     * Default: 2
     */
    BACKOFF_MULTIPLIER: 2,
  },
} as const;
